import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { getApiBaseUrl } from "../utils/apiConfig";

const isAuthenticated = () => {
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const expiry = localStorage.getItem("token_expiry");

  if (!userId || !userRole || !expiry) {
    return false;
  }

  // check expiry
  if (Date.now() > parseInt(expiry, 10)) {
    // session expired
    localStorage.clear(); // clear all session data
    return false;
  }

  const validRoles = [
    "merchant",
    "sub_merchant",
    "super_operation",
    "operation",
    "team_member",
    "super_merchant",
  ];
  return validRoles.includes(userRole);
};

// Function to refresh session expiry
const refreshSession = () => {
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const userEmail = localStorage.getItem("userEmail");

  if (userId && userRole && userEmail) {
    // Extend session by 8 hours from current time
    const newExpiryTime = Date.now() + 8 * 60 * 60 * 1000; // 8 hours
    localStorage.setItem("token_expiry", newExpiryTime);

  }
};

// Fetch profile to verify token validity
const fetchProfile = async (userId, navigate) => {
  try {
    if (!userId) {
      return false;
    }

    // Sub-users are not merchants — skip the merchant profile check
    const isSubUser = localStorage.getItem("isSubUser") === "true";
    if (isSubUser) {
      return true;
    }

    const API_BASE_URL = getApiBaseUrl();
    const response = await fetch(`${API_BASE_URL}/merchant/${userId}`, {
      method: "GET",
      credentials: "include",
      // headers: {
      //   "Content-Type": "application/json",
      //   Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
      // },
    });

    // Check if token is expired or invalid
    if (response.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.clear();
      sessionStorage.clear();
      if (navigate) {
        navigate("/", { replace: true });
      }
      return false;
    }

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.success === true;
  } catch (error) {
    console.error("Error fetching profile:", error);
    // On error, clear storage and redirect to login
    localStorage.clear();
    sessionStorage.clear();
    if (navigate) {
      navigate("/", { replace: true });
    }
    return false;
  }
};

const AuthGuard = ({ children }) => {
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    let refreshInterval = null;
    let isMounted = true;

    const validateAuth = async () => {
      // First check local authentication
      if (!isAuthenticated()) {
        if (isMounted) {
          setIsValidating(false);
          setIsValid(false);
        }
        return;
      }

      // Then verify token with server by fetching profile
      const userId = localStorage.getItem("userId");
      const profileValid = await fetchProfile(userId, navigate);

      if (!profileValid) {
        if (isMounted) {
          setIsValidating(false);
          setIsValid(false);
        }
        return;
      }

      // Token is valid, refresh session
      refreshSession();
      if (isMounted) {
        setIsValidating(false);
        setIsValid(true);
      }

      // Set up periodic session refresh (every 30 minutes)
      refreshInterval = setInterval(async () => {
        if (isAuthenticated()) {
          const profileStillValid = await fetchProfile(userId, navigate);
          if (profileStillValid) {
            refreshSession();
          } else {
            if (refreshInterval) {
              clearInterval(refreshInterval);
            }
          }
        }
      }, 30 * 60 * 1000); // 30 minutes
    };

    validateAuth();

    // Cleanup interval on unmount
    return () => {
      isMounted = false;
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [navigate]);

  // Show loading state while validating
  if (isValidating) {
    return null; // or a loading spinner
  }

  // Redirect to login if not authenticated
  if (!isValid) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default AuthGuard;
